/**
 * Hook for restoring MSSQL databases from .bak files (native T-SQL).
 * Uses TanStack Query mutation for async state management.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mssqlRestoreBak } from '../services/backupRestoreApi';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { useDatabaseStore } from '../../database-objects/store/databaseStore';
import type { MssqlRestoreBakRequest, MssqlRestoreBakResult } from '../types/backupRestore';
import { useAnalytics } from '../../../core/analytics';

type UseRestoreBakOptions = {
  onOperationCreated?: (operationId: string) => void;
  onSuccess?: (result: MssqlRestoreBakResult, operationId: string) => void;
  onError?: (error: Error) => void;
  /** Called when the database list should be refreshed (after successful restore) */
  onRefreshDatabases?: (connectionId: string) => void;
};

/**
 * Hook for initiating MSSQL .bak restore operations.
 * Tracks progress in the backup/restore store.
 */
export function useRestoreBak(options: UseRestoreBakOptions = {}) {
  const { addOperation, updateOperation } = useBackupRestoreStore();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const clearDatabasesFolderChildren = useDatabaseStore((state) => state.clearDatabasesFolderChildren);

  const mutation = useMutation({
    mutationFn: async (request: MssqlRestoreBakRequest): Promise<{ result: MssqlRestoreBakResult; operationId: string; connectionId: string }> => {
      // Track restore initiation
      track('feature:backup_restore', { engine: 'mssql' });

      // Add operation to store before starting
      const operationId = addOperation({
        type: 'restore-bak',
        connectionId: request.connectionId,
        databaseName: request.targetDatabaseName,
        filePath: request.serverFilePath,
      });

      options.onOperationCreated?.(operationId);

      try {
        // Update status to in progress
        updateOperation(operationId, {
          taskId: operationId,
          status: 'inProgress',
          message: 'Restore starting...',
        });

        const result = await mssqlRestoreBak({ ...request, taskId: operationId });

        if (result.success) {
          updateOperation(operationId, {
            status: 'succeeded',
            message: `Restore completed in ${result.duration ? `${(result.duration / 1000).toFixed(1)}s` : 'unknown time'}`,
            completedAt: new Date().toISOString(),
          });
        } else if (result.cancelled) {
          updateOperation(operationId, {
            status: 'cancelled',
            message: 'Cancelled by user',
            completedAt: new Date().toISOString(),
          });
        } else {
          updateOperation(operationId, {
            status: 'failed',
            error: result.error,
            completedAt: new Date().toISOString(),
          });
        }

        return { result, operationId, connectionId: request.connectionId };
      } catch (error) {
        updateOperation(operationId, {
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
          completedAt: new Date().toISOString(),
        });
        throw error;
      }
    },
    onSuccess: (data) => {
      // Refresh the database list after successful restore
      if (data.result.success) {
        // Invalidate the query cache
        void queryClient.invalidateQueries({
          queryKey: ['database-objects', 'databases', data.connectionId],
        });
        // Trigger refresh via callback if provided (handles proper data loading)
        // Otherwise fall back to clearing children (may show skeleton without loading)
        if (options.onRefreshDatabases) {
          options.onRefreshDatabases(data.connectionId);
        } else {
          clearDatabasesFolderChildren(data.connectionId);
        }
      }
      options.onSuccess?.(data.result, data.operationId);
    },
    onError: (error: Error) => {
      options.onError?.(error);
    },
  });

  return {
    restoreDatabase: mutation.mutate,
    restoreDatabaseAsync: mutation.mutateAsync,
    isRestoring: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
